import { useState, type ReactNode } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Pagination, PaginationContent, PaginationItem } from "@/components/ui/pagination";
import { IntentButton } from "./intent-button";
import { EmptyState } from "./empty-state";
import { LoadingState } from "./loading-state";
import { cn } from "@/lib/utils";

export type SortDirection = "asc" | "desc";

export interface DataGridColumn<T> {
  id: string;
  header: ReactNode;
  accessor: (row: T) => ReactNode;
  sortable?: boolean;
  align?: "left" | "right" | "center";
  width?: string;
}

export interface DataGridProps<T> {
  rows: readonly T[];
  columns: readonly DataGridColumn<T>[];
  rowKey: (row: T) => string | number;
  totalRows: number;
  pageIndex: number;
  pageSize: number;
  loading?: boolean;
  emptyHeadline?: string;
  onPageChange: (next: number) => void;
  onSortChange?: (sort: { columnId: string; direction: SortDirection }) => void;
}

export function DataGrid<T>({
  rows,
  columns,
  rowKey,
  totalRows,
  pageIndex,
  pageSize,
  loading,
  emptyHeadline = "No data",
  onPageChange,
  onSortChange,
}: DataGridProps<T>) {
  // Sort state is uncontrolled. Parents drive server-side sort via onSortChange;
  // to reset the indicator (e.g. from a "Clear filters" action), remount this
  // component (e.g. change its `key`).
  const [sort, setSort] = useState<{ columnId: string; direction: SortDirection } | null>(null);
  const lastPage = Math.max(0, Math.ceil(totalRows / pageSize) - 1);

  const toggleSort = (columnId: string) => {
    const next: SortDirection =
      sort?.columnId === columnId && sort.direction === "asc" ? "desc" : "asc";
    const value = { columnId, direction: next };
    setSort(value);
    onSortChange?.(value);
  };

  // Loading state hides header + pagination via early return because the
  // server-driven sort/page are in-flight and an interactive header would be
  // misleading. The empty state (below) does NOT do this — it renders inside
  // the table body so the user can still try a different sort/filter.
  if (loading) return <LoadingState />;

  return (
    <div className="flex flex-col gap-2">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((col) => {
                const ariaSort: "none" | "ascending" | "descending" | undefined = col.sortable
                  ? sort?.columnId === col.id
                    ? sort.direction === "asc"
                      ? "ascending"
                      : "descending"
                    : "none"
                  : undefined;
                const headerLabel = typeof col.header === "string" ? col.header : col.id;
                return (
                  <TableHead
                    key={col.id}
                    aria-sort={ariaSort}
                    style={col.width ? { width: col.width } : undefined}
                    className={cn(col.align === "right" && "text-right")}
                  >
                    {col.sortable ? (
                      <button
                        type="button"
                        aria-label={`Sort by ${headerLabel}`}
                        className="inline-flex items-center gap-1"
                        onClick={() => toggleSort(col.id)}
                      >
                        {col.header}
                        {sort?.columnId === col.id ? (
                          sort.direction === "asc" ? (
                            <ChevronUp className="size-3" />
                          ) : (
                            <ChevronDown className="size-3" />
                          )
                        ) : null}
                      </button>
                    ) : (
                      col.header
                    )}
                  </TableHead>
                );
              })}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="p-0">
                  <EmptyState headline={emptyHeadline} />
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={rowKey(row)} className="h-[var(--row-height)]">
                  {columns.map((col) => (
                    <TableCell key={col.id} className={cn(col.align === "right" && "text-right")}>
                      {col.accessor(row)}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      {totalRows > 0 ? (
        <Pagination className="justify-end">
          <PaginationContent>
            <PaginationItem>
              <IntentButton intent="ghost" onClick={() => onPageChange(pageIndex - 1)} disabled={pageIndex === 0}>
                Previous
              </IntentButton>
            </PaginationItem>
            <PaginationItem>
              <span className="px-3 text-sm text-muted-foreground">
                Page {pageIndex + 1} of {lastPage + 1}
              </span>
            </PaginationItem>
            <PaginationItem>
              <IntentButton
                intent="ghost"
                onClick={() => onPageChange(pageIndex + 1)}
                disabled={pageIndex >= lastPage}
              >
                Next
              </IntentButton>
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      ) : null}
    </div>
  );
}
